// @ts-strict
import {type ReactElement} from 'react';
import {toast} from '@ezcater/tapas';
import useTranslation from 'next-translate/useTranslation';

import Modal from '@/components/ui/Modal';
import useTracking from '@/hooks/useTracking';
import SupportMessageActions from './SupportMessageActions';
import SupportMessageFields from './SupportMessageFields';
import SupportMessageProvider from './SupportMessageProvider';
import {
  type FieldContext,
  type SubmitResult,
  type SupportCaseInput,
  type SupportMessageSurface,
} from './types';

type SupportMessageModalProps = {
  open: boolean;
  onClose: () => void;
  surface: Exclude<SupportMessageSurface, 'standalone_route'>;
  fieldContext: FieldContext;
  onSubmit: (input: SupportCaseInput) => Promise<SubmitResult>;
};

const SupportMessageModal = ({
  open,
  onClose,
  surface,
  fieldContext,
  onSubmit,
}: SupportMessageModalProps): ReactElement | null => {
  const {t} = useTranslation('common');
  const {track} = useTracking();

  if (!open) return null;

  const handleClose = () => {
    track('support-message-modal-closed', {
      page: window.location.pathname,
      misc_json: JSON.stringify({surface}),
    });
    onClose();
  };

  return (
    <SupportMessageProvider
      fieldContext={fieldContext}
      onSubmit={onSubmit}
      onSuccess={() => {
        toast.success(t('components.SupportMessage.successToast'));
        onClose();
      }}
      surface={surface}
    >
      <Modal
        data-testid="support-message-modal"
        footer={<SupportMessageActions className="px-6 py-4" onCancel={handleClose} />}
        onOpenChange={isOpen => !isOpen && handleClose()}
        open={open}
        size="medium"
        slotProps={{footer: {className: 'border-t'}}}
        title={t('components.SupportMessage.title')}
      >
        <SupportMessageFields />
      </Modal>
    </SupportMessageProvider>
  );
};

export default SupportMessageModal;
